/**
 * toolbox-resources-limiter.tsx — Resource limiter (přepis 2026-07-11, Filip).
 *
 * Skutečný limiter s hierarchií, jak ho backend umí:
 *   declared (module.json)  <  modul default  <  profil  <  container override
 * Efektivní limit = most-specific-wins. Tři úrovně řízení:
 *   1) Profily — pojmenované znovupoužitelné balíčky limitů (resources_profiles_*)
 *   2) Per-modul default — profil/limit pro všechny kontejnery modulu (resources_module_defaults)
 *   3) Per-container override — resources_set / resources_profile_apply
 * MCP-first: vše přes mcpCall (viz wiki dashboard-mcp-first).
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Cpu, HardDrive, KeyRound, Layers, MemoryStick, Pencil, Plus, Trash2 } from 'lucide-react';
import { callMcp as mcpCall } from './toolbox/scope';
import i18n from '../../lib/i18n';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { StatCard, StatsGrid } from '../../components/ui/Stats';
import { Button } from '../../components/ui/Button';
import { Badge, type BadgeVariant } from '../../components/ui/Badge';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Select } from '../../components/ui/Select';
import { Modal } from '../../components/ui/Modal';
import { EmptyState } from '../../components/ui/EmptyState';
import { ModulePanel } from '../../widgets/ModulePanel';
import { TOOLBOX_RESOURCES } from '../../widgets/module-panels';
import { RefreshButton } from '../../components/ui/RefreshButton';
import { themedConfirm } from '../../lib/themed-dialogs';

interface ContainerResources {
  cpus?: string | number;
  memory?: string;
  memory_swap?: string;
  pids_limit?: number;
  device_read_iops?: number;
  device_write_iops?: number;
}
interface Profile {
  id: string;
  name: string;
  label: string;
  description?: string;
  resources: ContainerResources;
  builtin?: boolean;
}
interface ResItem {
  id: string;
  module: string;
  label?: string;
  module_default: ContainerResources;
  profile: { name: string; resources: ContainerResources } | null;
  configured: ContainerResources;
  effective: ContainerResources;
  live: Record<string, unknown> | null;
}
interface Capacity {
  node: {
    cpu_cores: number;
    memory: { total_human: string; used_bytes: number; total_bytes: number };
    disk: { total_human: string; used_bytes: number; total_bytes: number };
  };
  allocated: { cpus: number; cpu_pct_of_node: number; memory_pct_of_node: number };
}
interface VaultLimitsResponse {
  limits: {
    max_item_bytes: number;
    max_entity_bytes: number;
    max_items_per_entity: number;
  };
  usage: { active_items: number; active_entities: number; stored_bytes: number };
}

const EMPTY: ContainerResources = {};
const has = (r?: ContainerResources) => !!r && Object.keys(r).length > 0;

/** Kompaktní zápis limitu: „2 CPU · 512m · 256 pids". */
function fmtRes(r?: ContainerResources): string {
  if (!has(r)) return '—';
  const p: string[] = [];
  if (r!.cpus != null && r!.cpus !== '') p.push(`${r!.cpus} CPU`);
  if (r!.memory) p.push(String(r!.memory));
  if (r!.pids_limit) p.push(`${r!.pids_limit} pids`);
  if (r!.device_read_iops || r!.device_write_iops) {
    p.push(`${r!.device_read_iops ?? 0}/${r!.device_write_iops ?? 0} IOPS`);
  }
  return p.length ? p.join(' · ') : '—';
}

function fmtBytes(value: number): string {
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(0)} KiB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MiB`;
}

/** Odkud pochází efektivní limit (nejkonkrétnější zdroj). */
function sourceOf(item: ResItem): { label: string; variant: BadgeVariant } {
  if (has(item.configured)) {
    return { label: i18n.t('toolboxResourcesLimiter.sourceOverride'), variant: 'purple' };
  }
  if (item.profile) {
    return {
      label: i18n.t('toolboxResourcesLimiter.sourceProfile', { name: item.profile.name }),
      variant: 'blue',
    };
  }
  if (has(item.module_default)) {
    return { label: i18n.t('toolboxResourcesLimiter.sourceModuleDefault'), variant: 'green' };
  }
  return { label: i18n.t('toolboxResourcesLimiter.sourceDeclared'), variant: 'gray' };
}

export function ResourceTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [editProfile, setEditProfile] = useState<Profile | 'new' | null>(null);
  const [overrideFor, setOverrideFor] = useState<ResItem | null>(null);
  const [moduleDefFor, setModuleDefFor] = useState<string | null>(null);
  const [vaultLimitsOpen, setVaultLimitsOpen] = useState(false);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['res-containers'] });
    qc.invalidateQueries({ queryKey: ['res-profiles'] });
    qc.invalidateQueries({ queryKey: ['res-capacity'] });
    qc.invalidateQueries({ queryKey: ['identity-vault-limits'] });
  };

  const { data: cap } = useQuery({
    queryKey: ['res-capacity'],
    queryFn: () =>
      mcpCall<{ node: Capacity['node']; allocated: Capacity['allocated'] }>(
        'toolbox.resources_capacity',
      ),
    staleTime: 15_000,
  });
  const { data: profData } = useQuery({
    queryKey: ['res-profiles'],
    queryFn: () => mcpCall<{ profiles: Profile[] }>('toolbox.resources_profiles_list'),
    staleTime: 30_000,
  });
  const { data: contData, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['res-containers'],
    queryFn: () => mcpCall<{ items: ResItem[] }>('toolbox.resources_list', { include_live: true }),
    staleTime: 5_000,
  });
  const { data: vaultData } = useQuery({
    queryKey: ['identity-vault-limits'],
    queryFn: () => mcpCall<VaultLimitsResponse>('identity.vault_limits_get'),
    staleTime: 30_000,
  });

  const profiles = profData?.profiles ?? [];
  const items = contData?.items ?? [];

  // ── Mutace ────────────────────────────────────────────────────────────────
  const applyProfile = useMutation({
    mutationFn: (a: { container_id: string; profile_name: string }) =>
      mcpCall('toolbox.resources_profile_apply', a),
    onSuccess: () => {
      toast.success(t('toolboxResourcesLimiter.profileAssignedToast'));
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const setOverride = useMutation({
    mutationFn: (a: { container_id: string; patch: ContainerResources }) =>
      mcpCall('toolbox.resources_set', { ...a, apply_live: true }),
    onSuccess: () => {
      toast.success(t('toolboxResourcesLimiter.overrideSavedToast'));
      invalidate();
      setOverrideFor(null);
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const saveProfile = useMutation({
    mutationFn: (
      p: {
        name: string;
        label: string;
        description?: string;
        resources: ContainerResources;
        isNew: boolean;
      },
    ) =>
      mcpCall(p.isNew ? 'toolbox.resources_profiles_create' : 'toolbox.resources_profiles_update', {
        name: p.name,
        label: p.label,
        description: p.description,
        resources: p.resources,
      }),
    onSuccess: () => {
      toast.success(t('toolboxResourcesLimiter.profileSavedToast'));
      invalidate();
      setEditProfile(null);
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const deleteProfile = useMutation({
    mutationFn: (name: string) => mcpCall('toolbox.resources_profiles_delete', { name }),
    onSuccess: () => {
      toast.success(t('toolboxResourcesLimiter.profileDeletedToast'));
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const setModuleDefault = useMutation({
    mutationFn: (a: { module: string; action: 'set' | 'clear'; patch?: ContainerResources }) =>
      mcpCall('toolbox.resources_module_defaults', a),
    onSuccess: () => {
      toast.success(t('toolboxResourcesLimiter.moduleDefaultSavedToast'));
      invalidate();
      setModuleDefFor(null);
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const setVaultLimits = useMutation({
    mutationFn: (limits: VaultLimitsResponse['limits']) =>
      mcpCall('identity.vault_limits_set', limits),
    onSuccess: () => {
      toast.success(t('toolboxResourcesLimiter.vaultLimitsSaved', 'Limity osobního šifrovaného úložiště byly uloženy'));
      invalidate();
      setVaultLimitsOpen(false);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // ── Kontejnery seskupené po modulu ──────────────────────────────────────────
  const byModule = useMemo(() => {
    const q = search.toLowerCase();
    // Backend občas vrátí item bez `module` → undefined key + localeCompare crash
    // (ui_crash 2026-08-06 /core/toolbox).
    const filtered = items.filter((it) => {
      const mod = String(it.module ?? '');
      if (!q) return true;
      return it.id.toLowerCase().includes(q) ||
        (it.label ?? '').toLowerCase().includes(q) ||
        mod.toLowerCase().includes(q);
    });
    const map = new Map<string, ResItem[]>();
    for (const it of filtered) {
      const mod = String(it.module ?? '').trim() || '_unknown';
      const arr = map.get(mod) ?? [];
      arr.push(it);
      map.set(mod, arr);
    }
    return [...map.entries()].sort((a, b) =>
      String(a[0] ?? '').localeCompare(String(b[0] ?? ''))
    );
  }, [items, search]);

  const memPct = cap?.allocated?.memory_pct_of_node ?? 0;

  return (
    <div className='space-y-5'>
      {/* 0) Node-switchable monitoring proužek — živé usage libovolného nodu */}
      <ModulePanel panel={TOOLBOX_RESOURCES} />

      {/* 1) Kapacita nodu (alokace, lokál) */}
      <StatsGrid columns={4}>
        <StatCard
          label={t('toolboxResourcesLimiter.statCpuCores')}
          value={cap?.node.cpu_cores ?? '—'}
          sub={t('toolboxResourcesLimiter.statCpuSub', {
            allocated: cap?.allocated.cpus ?? 0,
            pct: (cap?.allocated.cpu_pct_of_node ?? 0).toFixed(0),
          })}
          icon={Cpu}
        />
        <StatCard
          label={t('toolboxResourcesLimiter.statRam')}
          value={cap?.node.memory.total_human ?? '—'}
          sub={t('toolboxResourcesLimiter.statRamSub', { pct: memPct.toFixed(0) })}
          icon={MemoryStick}
          variant={memPct > 90 ? 'error' : memPct > 75 ? 'warning' : 'default'}
        />
        <StatCard
          label={t('toolboxResourcesLimiter.statDisk')}
          value={cap?.node.disk.total_human ?? '—'}
          icon={HardDrive}
        />
        <StatCard
          label={t('toolboxResourcesLimiter.statContainers')}
          value={items.length}
          sub={t('toolboxResourcesLimiter.statContainersSub', { count: byModule.length })}
          icon={Layers}
        />
      </StatsGrid>

      {/* Systémové limity, které nejsou Docker/container resources. */}
      <Card>
        <CardHeader>
          <div className='flex items-center justify-between gap-3'>
            <CardTitle icon={KeyRound}>{t('toolboxResourcesLimiter.vaultTitle', 'Osobní šifrované úložiště entit')}</CardTitle>
            <Button variant='secondary' size='sm' onClick={() => setVaultLimitsOpen(true)}>
              {t('toolboxResourcesLimiter.setLimits', 'Nastavit limity')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className='grid grid-cols-2 md:grid-cols-5 gap-4 text-sm'>
            <div>
              <div className='text-gray-500'>{t('toolboxResourcesLimiter.oneItem', 'Jedna položka')}</div>
              <div className='font-mono text-gray-200'>
                {vaultData ? fmtBytes(vaultData.limits.max_item_bytes) : '—'}
              </div>
            </div>
            <div>
              <div className='text-gray-500'>{t('toolboxResourcesLimiter.oneEntity', 'Jedna entita')}</div>
              <div className='font-mono text-gray-200'>
                {vaultData ? fmtBytes(vaultData.limits.max_entity_bytes) : '—'}
              </div>
            </div>
            <div>
              <div className='text-gray-500'>{t('toolboxResourcesLimiter.itemsPerEntity', 'Položek / entita')}</div>
              <div className='font-mono text-gray-200'>
                {vaultData?.limits.max_items_per_entity ?? '—'}
              </div>
            </div>
            <div>
              <div className='text-gray-500'>{t('toolboxResourcesLimiter.activeEntities', 'Aktivní entity')}</div>
              <div className='font-mono text-gray-200'>
                {vaultData?.usage.active_entities ?? '—'}
              </div>
            </div>
            <div>
              <div className='text-gray-500'>{t('toolboxResourcesLimiter.stored', 'Uloženo')}</div>
              <div className='font-mono text-gray-200'>
                {vaultData ? fmtBytes(vaultData.usage.stored_bytes) : '—'} ·{' '}
                {t('toolboxResourcesLimiter.itemsCount', '{{count}} položek', { count: vaultData?.usage.active_items ?? 0 })}
              </div>
            </div>
          </div>
          <p className='text-xs text-gray-500 mt-3'>
            {t(
              'toolboxResourcesLimiter.vaultHint',
              'Vault vzniká až prvním zápisem a patří výhradně Identity entitě. Limity vynucuje backend; root remap je oddělená auditovaná operace.',
            )}
          </p>
        </CardContent>
      </Card>

      {/* 2) Profily */}
      <Card>
        <CardHeader>
          <div className='flex items-center justify-between'>
            <CardTitle icon={Layers}>{t('toolboxResourcesLimiter.profilesTitle')}</CardTitle>
            <Button
              variant='secondary'
              size='sm'
              icon={<Plus size={14} />}
              onClick={() => setEditProfile('new')}
            >
              {t('toolboxResourcesLimiter.newProfileButton')}
            </Button>
          </div>
        </CardHeader>
        <CardContent className='p-0'>
          {profiles.length === 0
            ? <EmptyState icon={Layers} title={t('toolboxResourcesLimiter.noProfiles')} />
            : (
              <div className='divide-y divide-gray-800/50'>
                {profiles.map((p) => (
                  <div key={p.id} className='flex items-center gap-3 px-4 py-2.5'>
                    <div className='min-w-0'>
                      <div className='flex items-center gap-2'>
                        <span className='text-sm font-medium text-white'>{p.label}</span>
                        {p.builtin && (
                          <Badge variant='gray'>{t('toolboxResourcesLimiter.builtinBadge')}</Badge>
                        )}
                      </div>
                      {p.description && (
                        <div className='text-xs text-gray-500 truncate'>{p.description}</div>
                      )}
                    </div>
                    <span className='ml-auto text-xs text-gray-400 font-mono'>
                      {fmtRes(p.resources)}
                    </span>
                    <Button
                      variant='ghost'
                      size='icon'
                      icon={<Pencil size={13} />}
                      onClick={() =>
                        setEditProfile(p)}
                      title={t('toolboxResourcesLimiter.editTooltip')}
                    />
                    {!p.builtin && (
                      <Button
                        variant='ghost'
                        size='icon'
                        icon={<Trash2 size={13} />}
                        onClick={async () => {
                          if (
                            await themedConfirm(
                              t('toolboxResourcesLimiter.confirmDeleteProfile', { label: p.label }),
                            )
                          ) {
                            deleteProfile.mutate(p.name);
                          }
                        }}
                        title={t('toolboxResourcesLimiter.deleteTooltip')}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
        </CardContent>
      </Card>

      {/* 3) Kontejnery per modul + per-container override */}
      <Card>
        <CardHeader>
          <div className='flex items-center justify-between gap-3 flex-wrap'>
            <CardTitle icon={Cpu}>{t('toolboxResourcesLimiter.containersTitle')}</CardTitle>
            <div className='flex items-center gap-2'>
              <Input
                placeholder={t('toolboxResourcesLimiter.searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className='w-48'
              />
              <RefreshButton onRefresh={() => refetch()} loading={isFetching} />
            </div>
          </div>
        </CardHeader>
        <CardContent className='p-0'>
          {isLoading
            ? <div className='p-6 text-sm text-gray-500'>{t('common.loading')}</div>
            : byModule.length === 0
            ? <EmptyState icon={Cpu} title={t('toolboxResourcesLimiter.noContainers')} />
            : byModule.map(([module, rows]) => (
              <div key={module} className='border-b border-gray-800/40 last:border-0'>
                {/* Modul hlavička + per-modul default */}
                <div className='flex items-center gap-2 px-4 py-2 bg-gray-900/40'>
                  <Badge variant='gray'>{module}</Badge>
                  <span className='text-xs text-gray-600'>
                    {t('toolboxResourcesLimiter.containersCount', { count: rows.length })}
                  </span>
                  <Button
                    variant='ghost'
                    size='sm'
                    className='ml-auto'
                    onClick={() =>
                      setModuleDefFor(module)}
                  >
                    {t('toolboxResourcesLimiter.moduleDefaultButton')}
                  </Button>
                </div>
                {rows.map((it) => {
                  const src = sourceOf(it);
                  return (
                    <div key={it.id} className='flex items-center gap-3 px-4 py-2 pl-6'>
                      <div className='min-w-0 flex-1'>
                        <div className='text-sm text-gray-200 truncate'>{it.label || it.id}</div>
                        <div className='text-xs text-gray-600 font-mono truncate'>{it.id}</div>
                      </div>
                      <div className='text-right'>
                        <div className='text-xs text-gray-300 font-mono'>
                          {fmtRes(it.effective)}
                        </div>
                        <Badge variant={src.variant}>{src.label}</Badge>
                      </div>
                      {/* přiřazení profilu */}
                      <div className='w-40'>
                        <Select
                          size='sm'
                          options={[
                            { value: '', label: t('toolboxResourcesLimiter.noProfileOption') },
                            ...profiles.map((p) => ({ value: p.name, label: p.label })),
                          ]}
                          value={it.profile?.name ?? ''}
                          onChange={(e) =>
                            applyProfile.mutate({
                              container_id: it.id,
                              profile_name: e.target.value,
                            })}
                        />
                      </div>
                      <Button
                        variant='ghost'
                        size='sm'
                        onClick={() => setOverrideFor(it)}
                      >
                        {t('toolboxResourcesLimiter.overrideButton')}
                      </Button>
                    </div>
                  );
                })}
              </div>
            ))}
        </CardContent>
      </Card>

      {editProfile && (
        <ProfileModal
          profile={editProfile === 'new' ? null : editProfile}
          onClose={() => setEditProfile(null)}
          onSave={(p) => saveProfile.mutate({ ...p, isNew: editProfile === 'new' })}
          saving={saveProfile.isPending}
        />
      )}
      {overrideFor && (
        <ResourceModal
          title={t('toolboxResourcesLimiter.overrideModalTitle', {
            name: overrideFor.label || overrideFor.id,
          })}
          initial={overrideFor.configured}
          onClose={() => setOverrideFor(null)}
          onSave={(patch) => setOverride.mutate({ container_id: overrideFor.id, patch })}
          saving={setOverride.isPending}
        />
      )}
      {moduleDefFor && (
        <ResourceModal
          title={t('toolboxResourcesLimiter.moduleDefaultModalTitle', { module: moduleDefFor })}
          initial={items.find((i) => i.module === moduleDefFor)?.module_default ?? EMPTY}
          onClose={() => setModuleDefFor(null)}
          onSave={(patch) =>
            setModuleDefault.mutate({ module: moduleDefFor, action: 'set', patch })}
          onClear={() => setModuleDefault.mutate({ module: moduleDefFor, action: 'clear' })}
          saving={setModuleDefault.isPending}
        />
      )}
      {vaultLimitsOpen && vaultData && (
        <VaultLimitsModal
          initial={vaultData.limits}
          saving={setVaultLimits.isPending}
          onClose={() => setVaultLimitsOpen(false)}
          onSave={(limits) => setVaultLimits.mutate(limits)}
        />
      )}
    </div>
  );
}

function VaultLimitsModal(
  { initial, saving, onClose, onSave }: {
    initial: VaultLimitsResponse['limits'];
    saving: boolean;
    onClose: () => void;
    onSave: (limits: VaultLimitsResponse['limits']) => void;
  },
) {
  const { t } = useTranslation();
  const [itemKiB, setItemKiB] = useState(String(initial.max_item_bytes / 1024));
  const [entityMiB, setEntityMiB] = useState(String(initial.max_entity_bytes / (1024 * 1024)));
  const [items, setItems] = useState(String(initial.max_items_per_entity));
  const submit = () => {
    const maxItem = Math.round(Number(itemKiB) * 1024);
    const maxEntity = Math.round(Number(entityMiB) * 1024 * 1024);
    const maxItems = Math.round(Number(items));
    if (![maxItem, maxEntity, maxItems].every(Number.isFinite)) {
      toast.error(t('toolboxResourcesLimiter.invalidLimits', 'Vyplň platné číselné limity'));
      return;
    }
    onSave({
      max_item_bytes: maxItem,
      max_entity_bytes: maxEntity,
      max_items_per_entity: maxItems,
    });
  };
  return (
    <Modal open onClose={onClose} title={t('toolboxResourcesLimiter.vaultLimitsTitle', 'Limity osobního šifrovaného úložiště')}>
      <div className='space-y-3'>
        <div>
          <Label>{t('toolboxResourcesLimiter.maxItemKib', 'Maximum jedné položky (KiB)')}</Label>
          <Input
            type='number'
            min='1'
            max='10240'
            value={itemKiB}
            onChange={(e) => setItemKiB(e.target.value)}
          />
        </div>
        <div>
          <Label>{t('toolboxResourcesLimiter.maxEntityMib', 'Maximum jedné entity (MiB)')}</Label>
          <Input
            type='number'
            min='0.0625'
            max='1024'
            step='0.0625'
            value={entityMiB}
            onChange={(e) => setEntityMiB(e.target.value)}
          />
        </div>
        <div>
          <Label>{t('toolboxResourcesLimiter.maxItemsEntity', 'Maximum položek jedné entity')}</Label>
          <Input
            type='number'
            min='1'
            max='10000'
            value={items}
            onChange={(e) => setItems(e.target.value)}
          />
        </div>
        <div className='flex gap-2 pt-2'>
          <Button variant='primary' onClick={submit} disabled={saving}>{t('common.save', 'Uložit')}</Button>
          <Button variant='ghost' onClick={onClose} className='ml-auto'>{t('common.cancel', 'Zrušit')}</Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Modal: editace ContainerResources (override / modul default) ──────────────
function ResourceModal(
  { title, initial, onClose, onSave, onClear, saving }: {
    title: string;
    initial: ContainerResources;
    onClose: () => void;
    onSave: (patch: ContainerResources) => void;
    onClear?: () => void;
    saving?: boolean;
  },
) {
  const { t } = useTranslation();
  const [cpus, setCpus] = useState(String(initial.cpus ?? ''));
  const [memory, setMemory] = useState(initial.memory ?? '');
  const [pids, setPids] = useState(String(initial.pids_limit ?? ''));

  const submit = () => {
    const patch: ContainerResources = {};
    patch.cpus = cpus.trim() || undefined;
    patch.memory = memory.trim() || undefined;
    patch.pids_limit = pids.trim() ? Number(pids) : undefined;
    onSave(patch);
  };

  return (
    <Modal open onClose={onClose} title={title}>
      <div className='space-y-3'>
        <div>
          <Label>{t('toolboxResourcesLimiter.fieldCpu')}</Label>
          <Input
            value={cpus}
            onChange={(e) => setCpus(e.target.value)}
            placeholder={t('toolboxResourcesLimiter.placeholderExample2')}
          />
        </div>
        <div>
          <Label>{t('toolboxResourcesLimiter.fieldMemory')}</Label>
          <Input
            value={memory}
            onChange={(e) => setMemory(e.target.value)}
            placeholder={t('toolboxResourcesLimiter.placeholderExample2g')}
          />
        </div>
        <div>
          <Label>{t('toolboxResourcesLimiter.fieldPids')}</Label>
          <Input
            value={pids}
            onChange={(e) => setPids(e.target.value)}
            placeholder={t('toolboxResourcesLimiter.placeholderExample512')}
          />
        </div>
        <div className='flex items-center gap-2 pt-2'>
          <Button variant='primary' onClick={submit} disabled={saving}>{t('common.save')}</Button>
          {onClear && (
            <Button variant='ghost' onClick={onClear} disabled={saving}>
              {t('toolboxResourcesLimiter.clearButton')}
            </Button>
          )}
          <Button variant='ghost' onClick={onClose} className='ml-auto'>
            {t('common.cancel')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Modal: profil (label + description + resources) ──────────────────────────
function ProfileModal(
  { profile, onClose, onSave, saving }: {
    profile: Profile | null;
    onClose: () => void;
    onSave: (
      p: { name: string; label: string; description?: string; resources: ContainerResources },
    ) => void;
    saving?: boolean;
  },
) {
  const { t } = useTranslation();
  const [name, setName] = useState(profile?.name ?? '');
  const [label, setLabel] = useState(profile?.label ?? '');
  const [description, setDescription] = useState(profile?.description ?? '');
  const [cpus, setCpus] = useState(String(profile?.resources.cpus ?? ''));
  const [memory, setMemory] = useState(profile?.resources.memory ?? '');
  const [pids, setPids] = useState(String(profile?.resources.pids_limit ?? ''));

  const submit = () => {
    if (!name.trim() || !label.trim()) {
      toast.error(t('toolboxResourcesLimiter.slugAndNameRequired'));
      return;
    }
    const resources: ContainerResources = {};
    if (cpus.trim()) resources.cpus = cpus.trim();
    if (memory.trim()) resources.memory = memory.trim();
    if (pids.trim()) resources.pids_limit = Number(pids);
    onSave({
      name: name.trim(),
      label: label.trim(),
      description: description.trim() || undefined,
      resources,
    });
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={profile
        ? t('toolboxResourcesLimiter.profileModalTitle', { label: profile.label })
        : t('toolboxResourcesLimiter.newProfileTitle')}
    >
      <div className='space-y-3'>
        <div>
          <Label>{t('toolboxResourcesLimiter.fieldSlug')}</Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={!!profile}
            placeholder={t('toolboxResourcesLimiter.placeholderSlugExample')}
          />
        </div>
        <div>
          <Label>{t('toolboxResourcesLimiter.fieldName')}</Label>
          <Input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder={t('toolboxResourcesLimiter.placeholderNameExample')}
          />
        </div>
        <div>
          <Label>{t('toolboxResourcesLimiter.fieldDescription')}</Label>
          <Input value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
        <div className='grid grid-cols-3 gap-2'>
          <div>
            <Label>{t('toolboxResourcesLimiter.fieldCpuShort')}</Label>
            <Input value={cpus} onChange={(e) => setCpus(e.target.value)} placeholder='2' />
          </div>
          <div>
            <Label>{t('toolboxResourcesLimiter.fieldMemoryShort')}</Label>
            <Input value={memory} onChange={(e) => setMemory(e.target.value)} placeholder='2g' />
          </div>
          <div>
            <Label>{t('toolboxResourcesLimiter.fieldPidsShort')}</Label>
            <Input value={pids} onChange={(e) => setPids(e.target.value)} placeholder='512' />
          </div>
        </div>
        <div className='flex items-center gap-2 pt-2'>
          <Button variant='primary' onClick={submit} disabled={saving}>{t('common.save')}</Button>
          <Button variant='ghost' onClick={onClose} className='ml-auto'>
            {t('common.cancel')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
